import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import CSVUploadWidget from '../components/ui/CSVUploadWidget';
import InlineHoldingForm from '../components/ui/InlineHoldingForm';

interface OnboardingProgress {
  hasBrokerAccounts: boolean;
  brokerCount: number;
  hasHoldings: boolean;
  holdingsCount: number;
  hasRules: boolean;
  rulesCount: number;
  alertsUsed: number;
  alertsTotal: number;
  subscriptionTier: string;
  loading: boolean;
}

const Dashboard: React.FC = () => {
  const [progress, setProgress] = useState<OnboardingProgress>({
    hasBrokerAccounts: false,
    brokerCount: 0,
    hasHoldings: false,
    holdingsCount: 0,
    hasRules: false,
    rulesCount: 0,
    alertsUsed: 0,
    alertsTotal: 5,
    subscriptionTier: 'basic',
    loading: true,
  });
  const [showCSVUploadModal, setShowCSVUploadModal] = useState(false);
  const navigate = useNavigate();

  // Extracted reusable function for fetching dashboard progress
  const fetchDashboardProgress = useCallback(async () => {
    try {
      // Fetch broker accounts, alerts, rules, and holdings in parallel
      const [brokerResponse, alertsQuotaResponse, rulesResponse, holdingsResponse] = await Promise.all([
        apiService.brokers.getAccounts().catch(() => ({ data: [] })),
        apiService.alerts.getQuota().catch(() => ({ data: { used: 0, total: 5, subscription_tier: 'basic' } })),
        apiService.positionRules.list().catch(() => ({ data: [] })),
        apiService.holdings.getAll().catch(() => ({ data: [] })),
      ]);

      const brokerAccounts = (brokerResponse as any).success ? (brokerResponse as any).data || [] : brokerResponse.data || [];
      const connectedAccounts = brokerAccounts.filter((account: any) => 
        account.account_status === 'CONNECTED'
      );

      const quotaData = alertsQuotaResponse.data || { used: 0, total: 5, subscription_tier: 'basic' };

      const allRules = (rulesResponse as any).success ? (rulesResponse as any).data || [] : rulesResponse.data || [];
      const holdings = (holdingsResponse as any).success ? (holdingsResponse as any).data || [] : holdingsResponse.data || [];

      // Filter rules to only count those for current holdings
      const relevantRules = allRules.filter((rule: any) => {
        // Check if this rule applies to any current holding
        return holdings.some((holding: any) => {
          const symbolMatch = rule.symbol === holding.symbol && rule.exchange === holding.exchange;
          const scopeMatch = rule.rule_scope === 'all_holdings' || 
                           rule.target_account_id === holding.broker_account_id;
          return symbolMatch && scopeMatch && rule.is_active;
        });
      });

      setProgress({
        hasBrokerAccounts: connectedAccounts.length > 0,
        brokerCount: connectedAccounts.length,
        hasHoldings: holdings.length > 0,
        holdingsCount: holdings.length,
        hasRules: relevantRules.length > 0,
        rulesCount: relevantRules.length,
        alertsUsed: quotaData.used,
        alertsTotal: quotaData.total,
        subscriptionTier: quotaData.subscription_tier,
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching dashboard progress:', error);
      setProgress(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchDashboardProgress();
  }, [fetchDashboardProgress]);

  if (progress.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  const steps = [] as any[];

  // BROKER INTEGRATION TEMPORARILY DISABLED - See BROKER_INTEGRATION_REVERT_GUIDE.md for re-enabling
  /*
  if (!progress.hasBrokerAccounts) {
    steps.push({
      id: 'broker',
      title: 'Connect Your Broker Account',
      description: 'Link your Zerodha, Angel One, or other broker accounts to sync your portfolio',
      completed: false,
      action: 'Connect Account',
      link: '/brokers',
      icon: '🔗',
    });
  }
  */

  if (!progress.hasHoldings) {
    steps.push({
      id: 'holdings',
      title: 'Add Your Holdings',
      description: 'Start by adding your first stock holding. We\'ve pre-filled an example for you.',
      completed: false,
      action: 'Add Holdings',
      link: '#',
      icon: '',
      showForm: true, // Flag to show the form directly in this step
    });
  }

  if (!progress.hasRules) {
    steps.push({
      id: 'diagnosis',
      title: 'Set Up Alerts',
      description: 'Use the Price Guard to quickly set up alerts, and you\'re done !',
      completed: false,
      action: 'Set Up Alerts',
      link: '/holdings?magic-setup=true',
      icon: '',
      disabled: !progress.hasHoldings,
      onClick: () => {
        // Always navigate to holdings page, even if no holdings exist
        navigate('/holdings?magic-setup=true');
      },
    });
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      <div className="max-w-7xl mx-auto p-3 sm:p-4 lg:p-6">
        <div className="space-y-8">
          {/* Header removed */}

          {/* Getting Started Steps */}
          {steps.length > 0 && (
            <div className="card card-elevated p-8 animate-slide-in">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Getting Started</h2>
                  <p className="text-slate-600">Let's set up your portfolio management in a few simple steps</p>
                </div>
              </div>
              
              <div className="space-y-6">
                {steps.map((step, index) => (
                  <div
                    key={step.id}
                    className={`relative p-6 rounded-xl border-2 transition-all duration-300 group ${
                      step.completed
                        ? 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 shadow-sm'
                        : step.disabled
                        ? 'border-slate-200 bg-gradient-to-br from-slate-50 to-gray-50 hover:border-slate-300'
                        : 'border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 hover:border-blue-300 hover:shadow-md'
                    }`}
                  >
                    {/* Progress Line */}
                    {index < steps.length - 1 && (
                      <div className="absolute left-8 top-16 w-0.5 h-8 bg-slate-200"></div>
                    )}
                    
                    {/* Row 1: Step Header - Step Number + Step Text */}
                    <div className="flex items-start space-x-4">
                      {/* Step Number/Status */}
                      <div className="flex-shrink-0 relative z-10">
                        {step.completed ? (
                          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        ) : step.disabled ? (
                          <div className="w-12 h-12 bg-gradient-to-br from-slate-400 to-gray-500 rounded-xl flex items-center justify-center">
                            <span className="text-lg font-bold text-white">{index + 1}</span>
                          </div>
                        ) : (
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                            <span className="text-lg font-bold text-white">{index + 1}</span>
                          </div>
                        )}
                      </div>

                      {/* Step Content */}
                      <div className="flex-grow min-w-0">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                          <div className="space-y-2">
                            <div className="flex items-center space-x-3">
                              <span className="text-3xl">{step.icon}</span>
                              <div>
                                <h3 className={`text-lg font-bold ${
                                  step.completed ? 'text-emerald-800' : step.disabled ? 'text-slate-700' : 'text-blue-800'
                                }`}>
                                  {step.title}
                                </h3>
                                {step.completed && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    Completed
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className={`text-sm leading-relaxed ${
                              step.completed ? 'text-emerald-700' : step.disabled ? 'text-slate-500' : 'text-blue-700'
                            }`}>
                              {step.description}
                            </p>
                          </div>

                          {/* Action Button - Only show if not showing form */}
                          {!step.showForm && (
                            <div className="flex-shrink-0">
                              {step.disabled ? (
                                <button
                                  onClick={step.onClick || (() => {})}
                                  className="btn btn-secondary px-6 py-2.5 opacity-60 cursor-not-allowed"
                                  title={!progress.hasHoldings ? "Add holdings first, then set up rules" : ""}
                                >
                                  {step.action}
                                </button>
                              ) : step.completed ? (
                                <Link
                                  to={step.link}
                                  className="btn bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white px-6 py-2.5 shadow-md hover:shadow-lg"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  View
                                </Link>
                              ) : (
                                <button
                                  onClick={step.onClick || (() => {})}
                                  className="btn btn-primary px-6 py-2.5 shadow-md hover:shadow-lg group"
                                >
                                  <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                  </svg>
                                  {step.action}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Form Widget - Full width spanning the entire step container */}
                    {step.showForm && step.id === 'holdings' && (
                      <div className="mt-6 p-4 bg-white/80 backdrop-blur-sm rounded-lg border border-blue-200 w-full">
                        <InlineHoldingForm
                          onSuccess={() => {
                            fetchDashboardProgress();
                          }}
                          showUploadButton={true}
                          showAddButton={true}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {progress.hasHoldings && (
              <>
                <Link to="/holdings" className="card group hover:shadow-lg transition-all duration-300 p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM8 15v-3a1 1 0 011-1h2a1 1 0 011 1v3H8z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Holdings</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {progress.holdingsCount}
                      </p>
                    </div>
                  </div>
                </Link>

                <Link to="/holdings" className="card group hover:shadow-lg transition-all duration-300 p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Active Rules</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {progress.rulesCount}
                      </p>
                    </div>
                  </div>
                </Link>
              </>
            )}
          </div>

        {/* CSV Upload Modal */}
        {showCSVUploadModal && (
          <CSVUploadWidget
            onUploadSuccess={() => {
              // Refresh progress to update holdings count
              fetchDashboardProgress();
              setShowCSVUploadModal(false);
            }}
            onClose={() => {
              setShowCSVUploadModal(false);
            }}
          />
        )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 